import type {
  BaseQueryFn,
  EndpointBuilder,
  FetchArgs,
} from "@reduxjs/toolkit/query/react";

export type BillingInterval = "monthly" | "annual";

export type UpdateSubscriptionRequest = {
  unitType: "builder_seat" | "deployed_app" | "ai_credits";
  quantity: number;
  billingInterval?: BillingInterval;
  aiCreditPackSize?: number;
};

type UpdateSubscriptionApiResponse = {
  data: {
    message: string;
  };
};

export type UpdateSubscriptionResponse =
  UpdateSubscriptionApiResponse["data"] & {
    success?: boolean;
  };

export type PlanSummaryResponse = {
  billingInterval: BillingInterval;
  currentPlanCredits: number;
  currentUsageCredits: number;
  cycleStart: string | null;
  cycleEnd: string | null;
  creditsPerSeat: number | null;
  pricePerSeatAnnual: number | null;
  pricePerSeatMonthly: number | null;
  deployedAppsUsed: number;
  deployedAppsLimit: number;
  deployedAppsIncluded: number;
  deployedAppsAdditional: number;
  deployedAppPriceMonthly: number | null;
  deployedAppPriceAnnual: number | null;
  subscriptionStatus: string | null;
};

type PlanSummaryApiResponse = {
  data: PlanSummaryResponse;
};

type ReduxApiBuilder<ReducerPath extends string = string> = EndpointBuilder<
  BaseQueryFn<string | FetchArgs, unknown, unknown>,
  string,
  ReducerPath
>;

type BillingApiSlice = {
  util: {
    updateQueryData: (
      endpoint: string,
      arg: void,
      updateFn: (draft: unknown) => void,
    ) => { undo: () => void };
    invalidateTags: (tags: string[]) => unknown;
  };
  injectEndpoints: (config: unknown) => unknown;
};

export type PreviewInvoiceLineChange = {
  unitType: "builder_seat" | "deployed_app" | "ai_credits";
  quantity: number;
  aiCreditPackSize?: number;
};

export type PreviewInvoiceRequest = {
  changes: PreviewInvoiceLineChange[];
  billingInterval?: BillingInterval;
};

export type PreviewInvoiceResponse = {
  proratedAmountDue: number;
};

export type CreatePortalSessionRequest = {
  flow?: "payment_method_update" | "subscription_cancel";
  returnTo?: string;
};

export type CreatePortalSessionResponse = {
  portalUrl: string;
};

export type BillingEndpointsApi = {
  useGetPlanSummaryQuery: unknown;
  useUpdateSubscriptionMutation: unknown;
  useCreatePortalSessionMutation: unknown;
  usePreviewInvoiceMutation: unknown;
};

export const PlanSummaryTag = "PlanSummary" as const;
export const BuilderSeatsTag = "BuilderSeats" as const;

/**
 * Injects billing endpoints (getPlanSummary, updateSubscription) into your API slice.
 * Includes optimistic updates for seat/credit changes and delayed refetch for Stripe webhook.
 *
 * @param api - Your reduxApiSlice (must have listBuilderSeats and getPlanSummary already injected)
 */
export function injectBillingEndpoints(api: unknown): BillingEndpointsApi {
  const slice = api as BillingApiSlice;
  return slice.injectEndpoints({
    endpoints: (builder: unknown) =>
      buildBillingEndpoints(builder as ReduxApiBuilder, slice),
    overrideExisting: false,
  }) as BillingEndpointsApi;
}

function buildBillingEndpoints<ReducerPath extends string>(
  builder: ReduxApiBuilder<ReducerPath>,
  api: BillingApiSlice,
) {
  return {
    getPlanSummary: builder.query<PlanSummaryResponse, void>({
      query: () => "v1/billing/plan-summary",
      transformResponse: (response: PlanSummaryApiResponse) => response.data,
      providesTags: [PlanSummaryTag],
    }),
    createPortalSession: builder.mutation<
      CreatePortalSessionResponse,
      CreatePortalSessionRequest
    >({
      query: (body) => ({
        url: "v1/billing/create-portal-session",
        method: "POST",
        body,
      }),
      transformResponse: (response: { data: CreatePortalSessionResponse }) =>
        response.data,
    }),
    previewInvoice: builder.mutation<
      PreviewInvoiceResponse,
      PreviewInvoiceRequest
    >({
      query: (body) => ({
        url: "v1/billing/preview-invoice",
        method: "POST",
        body,
      }),
      transformResponse: (response: { data: PreviewInvoiceResponse }) =>
        response.data,
    }),
    updateSubscription: builder.mutation<
      UpdateSubscriptionResponse,
      UpdateSubscriptionRequest
    >({
      query: (body) => ({
        url: "v1/billing/update-subscription",
        method: "POST",
        body,
      }),
      transformResponse: (response: UpdateSubscriptionApiResponse) =>
        response.data,
      invalidatesTags: [],
      ...{
        async onQueryStarted(
          arg: UpdateSubscriptionRequest,
          {
            dispatch,
            queryFulfilled,
          }: {
            dispatch: (action: unknown) => unknown;
            queryFulfilled: Promise<unknown>;
          },
        ) {
          const patchResults: { undo: () => void }[] = [];

          if (arg.unitType === "builder_seat") {
            const patch = dispatch(
              api.util.updateQueryData(
                "listBuilderSeats",
                undefined,
                (draft) => {
                  (
                    draft as { totalPurchasedSeats: number }
                  ).totalPurchasedSeats = arg.quantity;
                },
              ),
            ) as { undo: () => void };
            patchResults.push(patch);
          }

          if (arg.unitType === "ai_credits" && arg.aiCreditPackSize != null) {
            const creditsToAdd = arg.aiCreditPackSize * arg.quantity;
            const patch = dispatch(
              api.util.updateQueryData("getPlanSummary", undefined, (draft) => {
                (draft as { currentPlanCredits: number }).currentPlanCredits =
                  creditsToAdd;
              }),
            ) as { undo: () => void };
            patchResults.push(patch);
          }

          if (arg.unitType === "deployed_app") {
            const patch = dispatch(
              api.util.updateQueryData("getPlanSummary", undefined, (draft) => {
                (
                  draft as { deployedAppsAdditional: number }
                ).deployedAppsAdditional = arg.quantity;
              }),
            ) as { undo: () => void };
            patchResults.push(patch);
          }

          try {
            await queryFulfilled;
            const tagsToInvalidate =
              arg.unitType === "ai_credits"
                ? [PlanSummaryTag]
                : [BuilderSeatsTag, PlanSummaryTag];
            setTimeout(() => {
              void dispatch(api.util.invalidateTags(tagsToInvalidate));
            }, 1000);
          } catch {
            for (const p of patchResults) p.undo();
          }
        },
      },
    }),
  };
}
